import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy, SimpleChanges, OnChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { AppSettings } from "app/modules/shared/app.settings";
import { MiFormComponent } from "app/modules/shared/components/mi-form/mi-form.component";
import type { AttributeData } from "app/modules/shared/models/attribute.models";
import { Country } from "app/modules/shared/models/country";
import { Language } from "app/modules/shared/models/language";
import { CommonBindingDataService } from "app/modules/shared/services/common-binding-data.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { EntityService } from "app/modules/shared/services/entity.service";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from "primeng/tabview";


interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
}

interface AttributeValues {
  product_entity_id?: { name: string; id: string } | string;
  [key: string]: unknown;
}

interface ProductEntityData {
  product_name: string;
  [key: string]: unknown;
}

interface RelatedDataItem {
  entityCode: string;
  entityId: string;
  actionStatus: string;
  [key: string]: unknown;
}

interface TabChangeEvent {
  index: number;
  originalEvent: {
    target: {
      innerText: string;
    };
  };
  [key: string]: unknown;
}

interface EntityResponse {
  relatedData: RelatedDataItem[];
  [key: string]: unknown;
}

interface ProtocolProductData {
  forTenantCode: string;
  entityCode: string;
  countryCode: string;
  languageCode: string;
  data: Array<{
    attributeCode?: string;
    attributeValue: string;
    attributeId?: string;
  }>;
  relationshipData: Array<{
    entityRelationshipConfigId: string;
    otherEntityId: string;
  }>;
}

interface EntitiesData {
  countryCode: string;
  tenantCode: string;
}

@Component({
  selector: 'app-add-protocol-product',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    ProgressSpinnerModule
  ],
  templateUrl: './add-protocol-product.component.html',
  styleUrl: './add-protocol-product.component.scss'
})
export class AddProtocolProductComponent implements OnInit, OnDestroy, OnChanges {
  activeIndex: number = 0;
  data: AttributeData;
  protocolProductAttributeData: any;
  originalProtocolProductAttributeData: any;
  routePath: MenuItem[] = [];
  attributeLabels: any;
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: ColumnDefinition[] = [];
  protocolProductAttributeLength: number;
  protocolProductRelation: any;
  productProtocolProductRelation: any;
  attributeValues: AttributeValues = {};
  nextBtnLabel: string;
  previousBtnLabel: string;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveProtocolProductBtnLabel: string;
  isFirstTabSaved: boolean = false;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode: string;
  tabIndex: number;
  @Input() protocolEntityId: string;
  @Input() protocolProductId: string = '';
  @Input() isChangeReasonNotRequired: boolean = false;
  @Input() isEditNotAllowedSpecificStatus: boolean = false;
  @Input() showAddProtocolProductDialog;
  @Output() onToggleOverLay: EventEmitter<boolean> = new EventEmitter();
  isLoading: boolean = true;
  protocolAttributeId: string;
  productStatusAttributeId: string;
  productId: string;
  loadDialogView = false;
  header: string;

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Output() saveSuccess = new EventEmitter<void>();

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeEntityService();
    this.setRoutePath('details');
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.protocolProductId) {
      this.loadProtocolProductData();
    }
  }

  private initializeLabels() {
    this.header = this.protocolProductId ?
      this.cs.getLabel("protocol.lbl_edit_product") :
      this.cs.getLabel("protocol.lbl_add_product");
    this.nextBtnLabel = this.protocolProductId ?
      this.cs.getLabel("protocol.lbl_update_product") :
      this.cs.getLabel("protocol.lbl_add_product");
    this.previousBtnLabel = this.cs.getLabel("lbl_previous");
    this.saveProtocolProductBtnLabel = this.protocolProductId ?
      this.cs.getLabel("protocol.lbl_update_product") :
      this.cs.getLabel("protocol.lbl_add_product");
  }

  private initializeEntityService() {
    this.moduleName = AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.entityService.setRelatedData([]);
  }

  private loadProtocolProductData() {
    this.isLoading = true;
    if (this.protocolProductId) {
      this.entityService
        .getEntity(this.protocolProductId, AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe({
          next: (res: EntityResponse) => {
            if (res) {
              this.processProtocolProductResponse(res);
              this.isNew = false;
              const currentTab = this.protocolProductAttributeData?.tabs[this.activeIndex];
              if (currentTab) {
                this.setRoutePath(currentTab.tabCode);
              }
            }
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
    } else {
      this.getProductStatusAttributeId();
    }
  }

  private processProtocolProductResponse(data: EntityResponse) {
    this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as AttributeValues;

    const productData = data.relatedData.find(item => item.entityCode === AppSettings.ENTITY_CODE.PRODUCT);
    const productEntityData = this.cs.getOrgAttributeValues(productData) as unknown as ProductEntityData;
    const productAttributeId = this.attributeValues.product_entity_id as unknown as string;
    this.attributeValues.product_entity_id = { name: productEntityData.product_name, id: productAttributeId };
    this.getProductStatusAttributeId();
  }

  onTabChange(event: TabChangeEvent) {
    const currentTab = this.protocolProductAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (event.index === 0) {
      this.isFirstTabSaved = false;
    }
    if (!this.isFirstTabSaved && event.index > 0) {
      this.activeIndex = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please save details in first tab before proceeding'
      });
      return;
    }

    this.label = event.originalEvent.target.innerText.trim();
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_CODE.PROTOCOL_PRODUCT;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.entityService.getAttributeDefinition(entityType, viewCode).subscribe({
      next: (res: unknown) => {
        if (!res) {
          this.isLoading = false;
          return;
        }
        this.setProtocolProductView(res);
        const currentTab = this.protocolProductAttributeData.tabs[this.activeIndex];
        if (currentTab) {
          this.setRoutePath(currentTab.tabCode);
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private getProductStatusAttributeId() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PRODUCT, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW)
      .subscribe((filterResponse: unknown) => {
        if (filterResponse) {
          const productStatusAttribute = this.cs.getOrganizedAttribute(filterResponse)?.tabs[0]?.groups[0]?.fields?.find(
            (element: { attributeCode: string }) => element.attributeCode === 'product_status')?.attributeId;
          if (productStatusAttribute) {
            this.productStatusAttributeId = productStatusAttribute;
            this.getAttributes();
          }
        }
      });
  }

  setProtocolProductView(res) {
    this.loadDialogView = true;
    this.attributeLabels = this.cs.getAttributeLabels(res);
    this.originalProtocolProductAttributeData = this.cs.getOrganizedAttribute(res);
    this.protocolAttributeId = this.originalProtocolProductAttributeData?.tabs[0]?.groups[0]?.fields?.find(
      (element: { attributeCode: string }) => element.attributeCode === 'protocol_entity_id')?.attributeId;

    this.protocolProductAttributeData = _.cloneDeep(this.originalProtocolProductAttributeData);

    this.protocolProductAttributeData.tabs = this.protocolProductAttributeData.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.filter((field: { attributeCode: string;[key: string]: unknown }) => {
          if (field.attributeCode === 'protocol_entity_id') {
            return false;
          }
          if (field.attributeCode === 'product_entity_id') {
            field.moduleName = AppSettings.ENTITY_CODE.PRODUCT;
            if (this.isEditNotAllowedSpecificStatus && this.protocolProductId) {
              field.disabled = true;
            }
            field.filter = [{
              attributeId: this.productStatusAttributeId,
              attributeValue: "Active"
            }];
            return true;
          }
          if (field.attributeCode === 'change_reason') {
            if (this.isChangeReasonNotRequired) {
              return false;
            } else {
              (field.validation as any).required = true;
              return true;
            }
          }
          if (field.attributeCode === 'upload_document') {
            field.documentURLs = this.attributeValues.upload_document_url;
          }
          return true;
        })
      }))
    }));

    this.protocolProductAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.protocolProductAttributeData?.tabs);
    this.protocolProductAttributeLength = this.protocolProductAttributeData.tabs.length;
    this.protocolProductRelation = _.find(res.relations, { otherEntityCode: 'protocol', relation: "oneToOne" });
    this.productProtocolProductRelation = _.find(res.relations, { otherEntityCode: 'product', relation: "oneToOne" });
  }

  onPreviousClick() {
    this.activeIndex = this.activeIndex - 1;
  }

  setRoutePath(tabCode: string) {
    this.routePath = [
      {
        label: this.cs.getLabel("protocol.lbl_protocol"),
        routerLink: '/app/trials',
        icon: 'pi pi-arrow-left',
        iconStyle: { 'font-weight': 'bold', 'margin-right': '10px' }
      },
      {
        label: this.cs.getLabel(tabCode),
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      }
    ];
  }

  onSaveProtocolProduct(event: Record<string, unknown>) {
    const protocolProductData: ProtocolProductData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language?.[0]?.langCode || 'en',
      data: [],
      relationshipData: []
    };

    const pData = event;
    for (const [key, value] of Object.entries(pData)) {
      if (value) {
        protocolProductData.data.push({
          attributeCode: key,
          attributeValue: value as string,
        });
      }
    }

    protocolProductData.data.push({
      attributeId: this.protocolAttributeId,
      attributeValue: this.protocolEntityId
    });

    this.productId = protocolProductData.data.find(item => item.attributeCode === 'product_entity_id')?.attributeValue as string;
    protocolProductData.relationshipData?.push({
      entityRelationshipConfigId: this.protocolProductRelation.entityRelationshipConfigId,
      otherEntityId: this.protocolEntityId
    }, {
      entityRelationshipConfigId: this.productProtocolProductRelation.entityRelationshipConfigId,
      otherEntityId: this.productId
    });

    const entitiesData: EntitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };

    if (!this.protocolProductId) {
      this.entityService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: any) => {
          this.saveProtocolProductData(protocolProductData, res.entityId, true);
        });
    } else {
      this.saveProtocolProductData(protocolProductData, this.protocolProductId, false);
    }
  }

  saveProtocolProductData(protocolProductData: ProtocolProductData, entityId: string, isNew: boolean) {
    this.isNew = isNew;
    protocolProductData.data = this.cs.mapAttributeIds(protocolProductData.data, this.originalProtocolProductAttributeData.tabs);
    this.entityService
      .saveAttributeData(protocolProductData.entityCode, entityId, protocolProductData).subscribe({
        next: () => {
          const success = this.cs.getLabelValue('product.message.success.create', this.attributeLabels, this.cs.getLabel('protocol.lbl_product_added_successfully'));
          const update = this.cs.getLabelValue('product.message.success.update', this.attributeLabels, this.cs.getLabel('protocol.lbl_product_updated_successfully'));
          if (this.protocolProductId) {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: update });
          } else {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: success });
          }
          this.reset();
          this.onHide();

          this.saveSuccess.emit();
        },
        error: () => {

        }
      });
  }

  onNextBtnClick() {
    if (this.protocolProductAttributeData?.tabs && this.protocolProductAttributeData.tabs.length > 0) {
      this.activeIndex = this.activeIndex + 1;
    }
  }

  onNextClick() {
    if (this.protocolProductAttributeData?.tabs && this.protocolProductAttributeData.tabs.length > 0) {
      this.activeIndex = this.activeIndex + 1;
    }
  }

  getLastIndex(): number {
    return this.protocolProductAttributeData?.tabs?.length - 1;
  }

  isLastIndex(): boolean {
    return this.activeIndex === this.getLastIndex();
  }

  onHide() {
    this.showAddProtocolProductDialog = false;
    this.onToggleOverLay.emit(this.showAddProtocolProductDialog);
  }

  onCancel() {
    this.showAddProtocolProductDialog = false;
    this.onToggleOverLay.emit(this.showAddProtocolProductDialog);
  }


  reset() {
    this.protocolProductAttributeData = null;
    this.originalProtocolProductAttributeData = null;
    this.attributeValues = {};
    this.activeIndex = 0;
    this.isFirstTabSaved = false;
    this.isNew = true;
    this.routePath = [];
    this.cols = [];
    this.protocolProductAttributeLength = 0;
    this.protocolProductRelation = null;
    this.productProtocolProductRelation = null;
    this.nextBtnLabel = '';
    this.previousBtnLabel = '';
    this.label = '';
    this.saveProtocolProductBtnLabel = '';
    this.tabCode = '';
    this.tabIndex = 0;
    this.protocolAttributeId = '';
    this.productStatusAttributeId = '';
    this.productId = '';
  }

  ngOnDestroy() {
    this.reset();
  }
} 